import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatCurrency, formatDate, formatNumber } from '@xdocs/i18n';
import type { Invoice, InvoiceStatus, PaddleReconciliationRow, SubscriptionStatus } from '@xdocs/schemas';
import { Badge, Card, DataTable, EmptyState, Skeleton, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone, ColumnDef, DataTableQuery } from '@xdocs/ui';
import { RoleGate } from '../../../features/admin/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { useAdminInvoicesList } from './useAdminInvoicesList.js';
import { useBillingOverview } from './useBillingOverview.js';
import { useBillingT } from './useBillingT.js';
import styles from './BillingPage.module.css';

const INVOICE_STATUS_TONE: Record<InvoiceStatus, BadgeTone> = {
  paid: 'success',
  open: 'info',
  void: 'neutral',
  refunded: 'warning',
};

function subscriptionStatusLabel(t: ReturnType<typeof useBillingT>['t'], status: SubscriptionStatus | null): string {
  return status ? t(`adminBilling:subscriptionStatus.${status}`) : t('adminBilling:subscriptionStatus.none');
}

/**
 * `/admin/billing` (F70, admin-only) — revenue overview (stat tiles from
 * `admin.billing.overview`), a subscriptions/Paddle-reconciliation table,
 * and an invoices `<DataTable>` (E4 §8.3). See `useAdminInvoicesList.ts` and
 * `useBillingOverview.ts` doc comments for the two frozen-contract
 * assumptions this page makes (no dedicated admin-scoped invoices op, no
 * unfiltered subscriptions-list op) — both flagged in this task's returned
 * open-issues list.
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task) — F70 is `admin`-only; see `PromosPage.tsx`'s doc comment for the
 * defense-in-depth rationale (UX-only, server enforces on every op).
 */
export function BillingPage(): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useBillingT();
  const overview = useBillingOverview();
  const invoices = useAdminInvoicesList();
  // `admin.billing.overview`'s `recentMismatches` isn't a paginated
  // `listQueryInput` resource (it's a bounded "most recent N" slice
  // embedded in the overview response) — there's no server op to page/sort
  // it against. `query`/`onQueryChange` below are local-only bookkeeping to
  // satisfy `<DataTable>`'s required props; omitting `total`/`searchLabel`
  // keeps the pagination footer and search box hidden since neither applies.
  const [reconciliationQuery, setReconciliationQuery] = useState<DataTableQuery>({ page: 1, pageSize: 20, sort: 'detectedAt', dir: 'desc' });

  const reconciliationColumns: ColumnDef<PaddleReconciliationRow>[] = [
    {
      id: 'userId',
      header: t('adminBilling:reconciliation.column.user'),
      primary: true,
      cell: (row) => <span className={cn(styles['mono'])}>{row.userId}</span>,
    },
    {
      id: 'localStatus',
      header: t('adminBilling:reconciliation.column.localStatus'),
      cell: (row) => <span>{subscriptionStatusLabel(t, row.localStatus)}</span>,
    },
    {
      id: 'paddleStatus',
      header: t('adminBilling:reconciliation.column.paddleStatus'),
      cell: (row) => <span>{subscriptionStatusLabel(t, row.paddleStatus)}</span>,
    },
    {
      id: 'detectedAt',
      header: t('adminBilling:reconciliation.column.detectedAt'),
      hideOnMobile: true,
      cell: (row) => <span className={cn(styles['mono'])}>{formatDate(locale, row.detectedAt, { dateStyle: 'medium' })}</span>,
    },
  ];

  const invoiceColumns: ColumnDef<Invoice>[] = [
    {
      id: 'issuedAt',
      header: t('adminBilling:invoices.list.column.issuedAt'),
      sortable: true,
      primary: true,
      cell: (row) => <span>{formatDate(locale, row.issuedAt, { dateStyle: 'medium' })}</span>,
    },
    {
      id: 'amount',
      header: t('adminBilling:invoices.list.column.amount'),
      cell: (row) => <span>{formatCurrency(locale, row.amount, row.currency)}</span>,
    },
    {
      id: 'status',
      header: t('adminBilling:invoices.list.column.status'),
      cell: (row) => <Badge tone={INVOICE_STATUS_TONE[row.status]}>{t(`adminBilling:invoiceStatus.${row.status}`)}</Badge>,
    },
    {
      id: 'paddleTransactionId',
      header: t('adminBilling:invoices.list.column.paddleTransactionId'),
      hideOnMobile: true,
      cell: (row) => <span className={cn(styles['mono'])}>{row.paddleTransactionId ?? '—'}</span>,
    },
  ];

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('adminBilling:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('adminBilling:page.description')}</p>
      </div>

      {overview.isLoading && (
        <div className={cn(styles['statRow'])} aria-hidden="true">
          <Skeleton shape="card" width="100%" height="88px" count={3} />
        </div>
      )}

      {!overview.isLoading && overview.isError && (
        <EmptyState kind="error" title={t('adminBilling:overview.error.title')} action={{ label: tCommon('datatable:error.retry'), onClick: overview.onRetry }} />
      )}

      {!overview.isLoading && !overview.isError && overview.data && (
        <>
          <div className={cn(styles['statRow'])}>
            <Card>
              <span className={cn(styles['statLabel'])}>{t('adminBilling:stat.mrr')}</span>
              <span className={cn(styles['statValue'])}>{formatCurrency(locale, overview.data.mrrMinor, overview.data.currency)}</span>
            </Card>
            <Card>
              <span className={cn(styles['statLabel'])}>{t('adminBilling:stat.activeSubscriptions')}</span>
              <span className={cn(styles['statValue'])}>{formatNumber(locale, overview.data.activeSubscriptions)}</span>
            </Card>
            <Card>
              <span className={cn(styles['statLabel'])}>{t('adminBilling:stat.churnRate')}</span>
              <span className={cn(styles['statValue'])}>{formatNumber(locale, overview.data.churnRatePercent / 100, { style: 'percent', maximumFractionDigits: 1 })}</span>
            </Card>
          </div>
          <p className={cn(styles['generatedAt'])}>
            {t('adminBilling:overview.generatedAt', { time: formatDate(locale, overview.data.generatedAt, { dateStyle: 'medium', timeStyle: 'short' }) })}
          </p>

          <section className={cn(styles['section'])}>
            <h2 className={cn(styles['sectionTitle'])}>{t('adminBilling:reconciliation.title')}</h2>
            <p className={cn(styles['sectionDescription'])}>{t('adminBilling:reconciliation.description')}</p>
            <DataTable
              columns={reconciliationColumns}
              rows={overview.data.recentMismatches}
              rowKey={(row) => `${row.userId}-${row.detectedAt.toISOString()}`}
              query={reconciliationQuery}
              onQueryChange={setReconciliationQuery}
              label={t('adminBilling:reconciliation.title')}
              emptyTitle={t('adminBilling:reconciliation.empty')}
              noResultsTitle={t('adminBilling:reconciliation.empty')}
            />
          </section>
        </>
      )}

      <section className={cn(styles['section'])}>
        <h2 className={cn(styles['sectionTitle'])}>{t('adminBilling:invoices.title')}</h2>
        <DataTable
          columns={invoiceColumns}
          rows={invoices.rows}
          rowKey={(row) => row.id}
          query={invoices.query}
          onQueryChange={invoices.onQueryChange}
          label={t('adminBilling:invoices.title')}
          isLoading={invoices.isLoading}
          isError={invoices.isError}
          onRetry={invoices.onRetry}
          total={invoices.total}
          nextCursor={invoices.nextCursor}
          onLoadMore={invoices.onLoadMore}
          isLoadingMore={invoices.isLoadingMore}
          {...optionalProp('loadMoreError', invoices.loadMoreError)}
          emptyTitle={t('adminBilling:invoices.list.empty.title')}
          emptyDescription={t('adminBilling:invoices.list.empty.description')}
          noResultsTitle={t('adminBilling:invoices.list.noResults.title', { query: invoices.query.q ?? '' })}
          onClearFilters={() => {
            const { q: _q, ...rest } = invoices.query;
            invoices.onQueryChange({ ...rest, page: 1 });
          }}
          searchLabel={t('adminBilling:invoices.list.searchLabel')}
          searchPlaceholder={t('adminBilling:invoices.list.searchPlaceholder')}
        />
      </section>
    </div>
    </RoleGate>
  );
}
